import { useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  TICKET_ATTACHMENT_MAX_BYTES,
  TICKET_ATTACHMENT_MIME_TYPES,
  TICKET_ATTACHMENTS_PER_MESSAGE,
  type TicketStaffView,
  type TicketTraceStatus,
} from '@traceio-org/platform-contracts';
import { describeWriteFailure, type WriteFailure } from '../../api';
import { MenuSelect } from '../../components/MenuSelect';
import { WriteFailureMessage } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { STATUS_OPTIONS } from './ticketFormat';
import { isAttachmentsOff } from './TicketThread';

type Kind = 'reply' | 'trace_note';

interface Upload {
  key: string;
  name: string;
  state: 'uploading' | 'ready' | 'failed';
  id?: string;
  error?: string;
}

const BODY_MAX = 10_000;
const KEEP_STATUS = '';

/**
 * The composer under the thread: a reply to the customer (they and their help desk read it) or a Trace note (only
 * Trace), with attachments (presigned upload, then finalize) and, for staff who work tickets, a status to set on send
 * (Waiting on customer after a question). ⌘ or Ctrl + Enter sends.
 */
export function TicketComposer({
  ticket,
  canSetStatus,
  onSent,
}: {
  ticket: TicketStaffView;
  canSetStatus: boolean;
  onSent: (kind: Kind) => void;
}) {
  const { api } = useAdmin();
  const fileInputId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<Kind>('reply');
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [setStatus, setSetStatus] = useState<TicketTraceStatus | ''>(KEEP_STATUS);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [attachmentsOff, setAttachmentsOff] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<WriteFailure | null>(null);

  const requester = ticket.requester.name ?? 'The requester';
  const placeholder =
    kind === 'reply' ? `${requester} and their help desk will see this` : 'Only Trace staff see notes';

  const update = (key: string, patch: Partial<Upload>) =>
    setUploads((current) => current.map((u) => (u.key === key ? { ...u, ...patch } : u)));

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      if (uploads.length >= TICKET_ATTACHMENTS_PER_MESSAGE) {
        setUploads((current) => [
          ...current,
          {
            key,
            name: file.name,
            state: 'failed',
            error: `At most ${TICKET_ATTACHMENTS_PER_MESSAGE} files per message.`,
          },
        ]);
        continue;
      }
      if (!(TICKET_ATTACHMENT_MIME_TYPES as readonly string[]).includes(file.type)) {
        setUploads((current) => [
          ...current,
          { key, name: file.name, state: 'failed', error: 'Images, PDFs and CSVs only.' },
        ]);
        continue;
      }
      if (file.size > TICKET_ATTACHMENT_MAX_BYTES) {
        setUploads((current) => [
          ...current,
          { key, name: file.name, state: 'failed', error: 'Larger than 10 MB.' },
        ]);
        continue;
      }
      setUploads((current) => [...current, { key, name: file.name, state: 'uploading' }]);
      try {
        const id = await api.uploadTicketAttachment(ticket.organizationId, file);
        update(key, { state: 'ready', id });
      } catch (error) {
        console.warn('admin-web: a ticket attachment upload failed', error);
        if (isAttachmentsOff(error)) {
          setAttachmentsOff(true);
          setUploads((current) => current.filter((u) => u.key !== key));
          return;
        }
        update(key, { state: 'failed', error: 'Upload failed, try again.' });
      }
    }
  };

  const send = async () => {
    if (sending) return;
    const text = body.trim();
    const error = !text
      ? 'Write a message.'
      : text.length > BODY_MAX
        ? `Write at most ${BODY_MAX} characters.`
        : null;
    setBodyError(error);
    if (error) return;
    if (uploads.some((u) => u.state === 'uploading')) {
      setBodyError('Wait for the attachments to finish uploading.');
      return;
    }
    setSending(true);
    setFailure(null);
    try {
      await api.replyToTicket(ticket.number, {
        kind,
        body: text,
        attachmentIds: uploads.filter((u) => u.state === 'ready' && u.id).map((u) => u.id!),
        ...(canSetStatus && setStatus ? { setStatus } : {}),
      });
    } catch (caught) {
      const described = describeWriteFailure(caught);
      if (described.kind !== 'saved-unreadable') {
        setFailure(described);
        setSending(false);
        return;
      }
    }
    setSending(false);
    setBody('');
    setUploads([]);
    setSetStatus(KEEP_STATUS);
    onSent(kind);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void send();
    }
  };

  const bodyErrorId = `${fileInputId}-body-error`;
  return (
    <section className="admin-ticket-composer" data-kind={kind} aria-label="Write a message">
      <div className="admin-segmented" role="group" aria-label="Message type">
        <button type="button" aria-pressed={kind === 'reply'} onClick={() => setKind('reply')}>
          Reply to customer
        </button>
        <button type="button" aria-pressed={kind === 'trace_note'} onClick={() => setKind('trace_note')}>
          Trace note
        </button>
      </div>
      {failure ? <WriteFailureMessage failure={failure} /> : null}
      <label className="admin-ticket-composer__field">
        <span className="admin-sr-only">Message</span>
        <textarea
          className="admin-input admin-input--area"
          rows={4}
          value={body}
          placeholder={placeholder}
          maxLength={BODY_MAX}
          onChange={(event) => setBody(event.currentTarget.value)}
          onKeyDown={onKeyDown}
          aria-invalid={bodyError ? true : undefined}
          aria-describedby={bodyError ? bodyErrorId : undefined}
        />
      </label>
      {bodyError ? (
        <p className="admin-field__error" id={bodyErrorId}>
          {bodyError}
        </p>
      ) : null}
      {uploads.length > 0 ? (
        <ul className="admin-bare-list admin-ticket-uploads" aria-label="Attachments to send">
          {uploads.map((upload) => (
            <li key={upload.key} data-state={upload.state}>
              <span>{upload.name}</span>
              <span className="admin-muted">
                {upload.state === 'uploading'
                  ? 'Uploading…'
                  : upload.state === 'failed'
                    ? upload.error
                    : 'Ready'}
              </span>
              <button
                type="button"
                className="admin-link-button"
                aria-label={`Remove ${upload.name}`}
                onClick={() => setUploads((current) => current.filter((u) => u.key !== upload.key))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="admin-ticket-composer__bar">
        {attachmentsOff ? (
          <span className="admin-muted" role="status">
            Attachments are off in this environment.
          </span>
        ) : (
          <>
            {/* The picker is opened by a real button (keyboard and screen readers); the input itself stays hidden. */}
            <input
              ref={fileInput}
              id={fileInputId}
              type="file"
              hidden
              multiple
              aria-label="Attach files"
              accept={TICKET_ATTACHMENT_MIME_TYPES.join(',')}
              onChange={(event) => {
                void addFiles(event.currentTarget.files);
                event.currentTarget.value = '';
              }}
            />
            <button type="button" className="admin-action" onClick={() => fileInput.current?.click()}>
              Attach files
            </button>
          </>
        )}
        {canSetStatus ? (
          <MenuSelect
            variant="compact"
            align="start"
            label="Set status on send"
            value={setStatus}
            options={[{ value: KEEP_STATUS, label: 'Keep status' }, ...STATUS_OPTIONS]}
            onChange={setSetStatus}
          />
        ) : null}
        <button
          type="button"
          className="admin-action admin-action--primary admin-ticket-composer__send"
          onClick={() => void send()}
          aria-disabled={sending || undefined}
          data-busy={sending || undefined}
        >
          {kind === 'reply' ? 'Send reply' : 'Add note'} <kbd className="admin-kbd">⌘↵</kbd>
        </button>
      </div>
    </section>
  );
}
